import { useCallback, useState } from 'react';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { archiveCustomer, unarchiveCustomer } from 'store/customers/client';

type CustomerMutationOptions = {
    successTitle: string;
    successDescription: string;
    onFinally?: () => void;
};

const useCustomerMutations = (refreshCustomers: () => Promise<any> | any) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [isMutating, setIsMutating] = useState(false);

    const mutateCustomer = useCallback(
        async (fn: () => Promise<any>, opts: CustomerMutationOptions) => {
            try {
                setIsMutating(true);
                showGlobalOverlay({ type: 'LoadingOverlay' });
                await fn();
                Notification({
                    type: 'success',
                    messageTitle: opts.successTitle,
                    description: opts.successDescription,
                    placement: 'top',
                });
                await refreshCustomers();
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Error',
                    description: getErrorMessage(error) || 'Something went wrong.',
                    placement: 'top',
                });
            } finally {
                setIsMutating(false);
                hideGlobalOverlay();
                opts.onFinally?.();
            }
        },
        [showGlobalOverlay, hideGlobalOverlay, refreshCustomers]
    );

    const archive = useCallback(
        async (customerId: string, opts?: Pick<CustomerMutationOptions, 'onFinally'>) => {
            return mutateCustomer(() => archiveCustomer(customerId), {
                successTitle: 'Customer Archived',
                successDescription: 'The customer has been successfully added to the archive.',
                onFinally: opts?.onFinally,
            });
        },
        [mutateCustomer]
    );

    const unarchive = useCallback(
        async (customerId: string, opts?: Pick<CustomerMutationOptions, 'onFinally'>) => {
            return mutateCustomer(() => unarchiveCustomer(customerId), {
                successTitle: 'Customer Unarchived',
                successDescription: 'The customer has been unarchived successfully.',
                onFinally: opts?.onFinally,
            });
        },
        [mutateCustomer]
    );

    return { isMutating, mutateCustomer, archive, unarchive };
};

export default useCustomerMutations;
